import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import ManualGeoFixModal from "@/components/ManualGeoFixModal";
import { Loader2, Upload, FileSpreadsheet, Calendar, MapPin, ArrowRight,
  Coffee, Pause as PauseIcon, Droplet, Sparkles, AlertCircle, ChevronDown,
  ChevronRight, Trash2, X, Eye, RefreshCw, Zap, CheckCircle2, MapPinned,
  Brain, BookmarkPlus, ShieldCheck, AlertTriangle, Bus, Link as LinkIcon,
  User as UserIcon, Layers, ArrowLeft, Wand2, History, Download, Globe2,
} from "lucide-react";

/**
 * Programmes client — Phase A
 * ───────────────────────────
 *   - List of imported driver-Excel programs
 *   - Upload wizard:  Drop file → Preview → Confirm
 *   - Detail view: weekly blocks (Lundi 5101, Mardi 5201, …) with stops + events
 *
 * Routes used:
 *   POST /api/programs/preview-excel              (multipart)
 *   POST /api/programs/import-excel/confirm        (JSON)
 *   GET  /api/programs
 *   GET  /api/programs/{template_id}
 *   DEL  /api/programs/{template_id}
 */

const DAY_NAMES = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"];

const EVENT_META = {
  "Coupure":    { icon: Coffee,     color: "#71717A", label: "Coupure" },
  "Réserve":    { icon: PauseIcon,  color: "#3B82F6", label: "Réserve" },
  "Plein":      { icon: Droplet,    color: "#10B981", label: "Plein" },
  "Lavage":     { icon: Sparkles,   color: "#6366F1", label: "Lavage" },
  "Nettoyage":  { icon: Sparkles,   color: "#A855F7", label: "Nettoyage" },
  "Pause":      { icon: Coffee,     color: "#71717A", label: "Pause" },
  "Pause repas":{ icon: Coffee,     color: "#71717A", label: "Pause repas" },
};

const WIZARD_STEPS = [
  { key: "drop",     label: "Excel",         num: 1 },
  { key: "preview",  label: "Aperçu",        num: 2 },
  { key: "services", label: "Services",      num: 3 },
  { key: "assign",   label: "Affecter",      num: 4 },
  { key: "generate", label: "Générer",       num: 5 },
  { key: "done",     label: "Confirmation",  num: 6 },
];

const INITIAL_WIZARD = {
  open: false, step: "drop",
  file: null, preview: null,
  committed: null,            // server response from /confirm
  assignments: {},            // tour_id -> {vehicle_id, driver_id} for inline assigns
  generation: null,           // result of /missions/generate
  busy: false,
};

export default function Programs() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [wizard, setWizard] = useState(INITIAL_WIZARD);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/programs");
      setItems(data.items || []);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="flex flex-col h-[calc(100vh-0px)] bg-zinc-100" data-testid="programs-page">
      {/* Header */}
      <header className="bg-zinc-900 text-white border-b-2 border-[#FFC60A] flex items-center px-3 py-2 gap-3 flex-shrink-0">
        <div className="flex items-center gap-2 mr-2">
          <div className="text-[9px] font-mono tracking-[0.25em] uppercase text-[#FFC60A]">LOGIBUS · Flotte</div>
          <div className="font-display font-black text-lg tracking-tighter">IMPORT TOURNÉES</div>
        </div>
        <div className="flex-1" />
        <div className="text-[10px] font-mono text-zinc-400 mr-2">
          {items.length} programme{items.length > 1 ? "s" : ""}
        </div>
        <button onClick={load} disabled={loading} data-testid="programs-refresh"
          className="px-2 py-1 border border-zinc-700 text-[10px] font-bold uppercase tracking-widest hover:bg-zinc-800 inline-flex items-center gap-1 disabled:opacity-50">
          {loading ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />} Refresh
        </button>
        <button onClick={() => setWizard({ ...INITIAL_WIZARD, open: true })}
          data-testid="programs-import-open"
          className="px-3 py-1 bg-[#FFC60A] text-zinc-900 border border-[#FFC60A] text-[11px] font-black uppercase tracking-widest hover:bg-yellow-300 inline-flex items-center gap-1.5">
          <Upload size={13} /> Importer un fichier
        </button>
      </header>

      {/* Body */}
      <div className="flex-1 overflow-y-auto p-4">
        {loading && items.length === 0 ? (
          <div className="text-center py-12 text-zinc-500"><Loader2 size={20} className="inline animate-spin mr-2" /> Chargement…</div>
        ) : items.length === 0 ? (
          <EmptyState onImport={() => setWizard({ ...INITIAL_WIZARD, open: true })} />
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="programs-grid">
            {items.map((p) => (
              <ProgramCard key={p.id} program={p} onOpen={() => setOpenId(p.id)} onRefresh={load} />
            ))}
          </div>
        )}

        {/* Historique d'imports + Géocodage en masse */}
        <div className="mt-6 grid grid-cols-1 xl:grid-cols-3 gap-4 max-w-7xl">
          <div className="xl:col-span-2"><ImportHistory refreshKey={items.length} /></div>
          <BulkGeocodePanel />
        </div>
      </div>

      {/* Import wizard modal */}
      {wizard.open && (
        <ImportWizard wizard={wizard} setWizard={setWizard}
          onDone={() => { setWizard(INITIAL_WIZARD); load(); }} />
      )}

      {/* Program detail drawer */}
      {openId && <ProgramDetail templateId={openId} onClose={() => setOpenId(null)} />}
    </div>
  );
}

/* ── Historique d'imports ──────────────────────────────────────────────── */
function ImportHistory({ refreshKey }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/programs/import-snapshots");
      setRows(data.items || []);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [refreshKey]);

  const download = async (snap) => {
    setDownloading(snap.id);
    try {
      const res = await api.get(`/programs/import-snapshots/${snap.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = snap.source_filename || `import_${snap.id.slice(0, 8)}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Téléchargement impossible — fichier original absent du snapshot"); }
    finally { setDownloading(null); }
  };

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleString("fr-CH", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch { return iso; }
  };

  return (
    <div className="bg-white border border-zinc-200 shadow-[4px_4px_0_0_#0A0A0B]" data-testid="import-history-panel">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-zinc-200 bg-zinc-900 text-white">
        <History size={14} className="text-[#FFC60A]" />
        <div className="text-[10px] font-mono tracking-[0.2em] uppercase">Historique d'imports</div>
        <div className="flex-1" />
        <button onClick={load} disabled={loading} data-testid="import-history-refresh"
          className="p-1 hover:bg-zinc-800 disabled:opacity-50">
          {loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
        </button>
      </div>
      {rows.length === 0 ? (
        <div className="px-3 py-6 text-center text-xs text-zinc-500" data-testid="import-history-empty">
          {loading ? "Chargement…" : "Aucun import enregistré. Chaque import Excel crée automatiquement un snapshot ici."}
        </div>
      ) : (
        <div className="divide-y divide-zinc-100 max-h-72 overflow-y-auto">
          {rows.map((s) => (
            <div key={s.id} className="flex items-center gap-3 px-3 py-2 hover:bg-zinc-50" data-testid={`import-history-row-${s.id}`}>
              <FileSpreadsheet size={16} className="text-emerald-600 flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="text-xs font-bold text-zinc-900 truncate">{s.source_filename || s.program_name || "Import sans nom"}</div>
                <div className="text-[10px] font-mono text-zinc-500 flex items-center gap-2 flex-wrap">
                  <span>{fmtDate(s.created_at)}</span>
                  {s.program_name && <span className="truncate">· {s.program_name}</span>}
                  {s.school_start && <span>· {s.school_start} → {s.school_end}</span>}
                </div>
              </div>
              <span className={`px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest border flex-shrink-0 ${s.engine === "epsp" ? "bg-[#FFC60A]/20 border-[#FFC60A] text-zinc-900" : "bg-zinc-100 border-zinc-300 text-zinc-600"}`}>
                {s.engine || "legacy"}
              </span>
              <button onClick={() => download(s)} disabled={!s.has_file || downloading === s.id}
                data-testid={`import-history-download-${s.id}`}
                title={s.has_file ? "Télécharger le fichier Excel original" : "Fichier original non conservé"}
                className="p-1.5 border border-zinc-300 hover:bg-zinc-900 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed flex-shrink-0">
                {downloading === s.id ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Géocodage en masse (Nominatim) ────────────────────────────────────── */
function BulkGeocodePanel() {
  const [status, setStatus] = useState(null);
  const [starting, setStarting] = useState(false);
  const [unresolved, setUnresolved] = useState([]);
  const [fixing, setFixing] = useState(null);
  const pollRef = useRef(null);

  const loadUnresolved = async () => {
    try {
      const { data } = await api.get("/geocoding/unresolved-stops");
      setUnresolved(data.items || []);
    } catch { /* silent */ }
  };

  const fetchStatus = async () => {
    try {
      const { data } = await api.get("/geocoding/bulk-stops/status");
      setStatus(data);
      if (!data.running && pollRef.current) {
        clearInterval(pollRef.current); pollRef.current = null;
        loadUnresolved();
        if (data.done > 0 && !data.error) {
          toast.success(`Géocodage terminé : ${data.ok} lieux localisés, ${data.failed} introuvables`);
        }
      }
      return data;
    } catch { return null; }
  };

  useEffect(() => {
    fetchStatus().then((d) => {
      if (d?.running && !pollRef.current) pollRef.current = setInterval(fetchStatus, 2000);
    });
    loadUnresolved();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  const start = async () => {
    setStarting(true);
    try {
      await api.post("/geocoding/bulk-stops/start");
      toast.info("Géocodage démarré en arrière-plan (Nominatim, 1 requête/sec)");
      if (!pollRef.current) pollRef.current = setInterval(fetchStatus, 2000);
      fetchStatus();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message); }
    finally { setStarting(false); }
  };

  const running = !!status?.running;
  const pct = status?.total ? Math.round((status.done / status.total) * 100) : 0;

  return (
    <div className="bg-white border border-zinc-200 shadow-[4px_4px_0_0_#0A0A0B] flex flex-col" data-testid="bulk-geocode-panel">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-zinc-200 bg-zinc-900 text-white">
        <Globe2 size={14} className="text-[#FFC60A]" />
        <div className="text-[10px] font-mono tracking-[0.2em] uppercase">Géocodage des arrêts</div>
      </div>
      <div className="p-3 flex-1 flex flex-col gap-3">
        <p className="text-[11px] text-zinc-600 leading-relaxed">
          Localise tous les arrêts et lieux des programmes importés via OpenStreetMap (Nominatim).
          Les résultats sont mis en cache — relancer est instantané.
        </p>

        {(running || (status && status.done > 0)) && (
          <div data-testid="bulk-geocode-progress">
            <div className="flex justify-between text-[10px] font-mono text-zinc-600 mb-1">
              <span>{status.done}/{status.total} lieux</span>
              <span>{pct}%</span>
            </div>
            <div className="h-2 bg-zinc-100 border border-zinc-200 overflow-hidden">
              <div className="h-full bg-[#FFC60A] transition-[width] duration-500" style={{ width: `${pct}%` }} />
            </div>
            <div className="flex gap-3 mt-1.5 text-[10px] font-mono">
              <span className="text-emerald-700">✓ {status.ok} trouvés</span>
              <span className="text-red-600">✗ {status.failed} introuvables</span>
            </div>
            {running && status.current && (
              <div className="text-[10px] font-mono text-zinc-500 mt-1 truncate">En cours : {status.current}</div>
            )}
            {!running && status.updated_template_days != null && (
              <div className="text-[10px] font-mono text-zinc-500 mt-1">
                {status.updated_template_days} trames + {status.updated_planned_services} services mis à jour
              </div>
            )}
            {status.error && <div className="text-[10px] text-red-600 mt-1">Erreur : {status.error}</div>}
          </div>
        )}

        {unresolved.length > 0 && (
          <div data-testid="unresolved-stops-list">
            <div className="text-[10px] font-mono uppercase tracking-widest text-red-700 mb-1.5">
              {unresolved.length} lieu{unresolved.length > 1 ? "x" : ""} sans coordonnées
            </div>
            <div className="max-h-44 overflow-y-auto divide-y divide-zinc-100 border border-zinc-200">
              {unresolved.map((u) => (
                <div key={u.name} className="flex items-center gap-2 px-2 py-1.5 hover:bg-zinc-50"
                  data-testid={`unresolved-stop-${u.name}`}>
                  <div className="min-w-0 flex-1">
                    <div className="text-[11px] font-bold text-zinc-800 truncate">{u.name}</div>
                    <div className="text-[9px] font-mono text-zinc-400">{u.occurrences} occurrence{u.occurrences > 1 ? "s" : ""}</div>
                  </div>
                  <button onClick={() => setFixing(u.name)}
                    data-testid={`unresolved-fix-${u.name}`}
                    className="px-2 py-1 border border-zinc-900 text-[9px] font-black uppercase tracking-widest hover:bg-[#FFC60A] inline-flex items-center gap-1 flex-shrink-0">
                    <MapPinned size={10} /> Corriger
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <button onClick={start} disabled={running || starting} data-testid="bulk-geocode-start"
          className="mt-auto px-3 py-2 bg-zinc-900 text-white border border-zinc-900 hover:bg-[#FFC60A] hover:text-zinc-900 text-[10px] font-black uppercase tracking-widest inline-flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
          {running ? <><Loader2 size={13} className="animate-spin" /> Géocodage en cours…</>
            : <><MapPinned size={13} /> Géocoder tous les arrêts</>}
        </button>
      </div>

      {fixing && (
        <ManualGeoFixModal name={fixing}
          onClose={() => setFixing(null)}
          onSaved={() => { setFixing(null); loadUnresolved(); }} />
      )}
    </div>
  );
}

function EmptyState({ onImport }) {
  return (
    <div className="max-w-xl mx-auto mt-12 text-center p-8 bg-white border border-zinc-200 shadow-[4px_4px_0_0_#0A0A0B]" data-testid="programs-empty">
      <FileSpreadsheet size={40} className="mx-auto text-zinc-400 mb-3" />
      <div className="font-display font-black text-2xl tracking-tighter mb-1">Aucun programme client</div>
      <p className="text-sm text-zinc-600 mb-5">
        Importez un fichier Excel chauffeur (Doc-Chauffeur) pour générer automatiquement la trame hebdomadaire du programme.
      </p>
      <button onClick={onImport} data-testid="programs-empty-import"
        className="px-4 py-2 bg-zinc-900 text-white border border-zinc-900 hover:bg-[#FFC60A] hover:text-zinc-900 text-[11px] font-black uppercase tracking-widest inline-flex items-center gap-2">
        <Upload size={14} /> Importer un fichier
      </button>
    </div>
  );
}

/* ── Program card ──────────────────────────────────────────────────────── */
function ProgramCard({ program, onOpen, onRefresh }) {
  const [deleting, setDeleting] = useState(false);
  const [confirming, setConfirming] = useState(false);
  // Auto-clear the inline confirm state after 4s if user doesn't act
  useEffect(() => {
    if (!confirming) return undefined;
    const t = setTimeout(() => setConfirming(false), 4000);
    return () => clearTimeout(t);
  }, [confirming]);
  const onDeleteClick = (e) => {
    e.stopPropagation();
    if (!confirming) { setConfirming(true); return; }
    setDeleting(true);
    api.delete(`/programs/${program.id}`)
      .then(() => { toast.success("Programme supprimé"); onRefresh(); })
      .catch((err) => toast.error(formatApiErrorDetail(err?.response?.data?.detail) || err.message))
      .finally(() => { setDeleting(false); setConfirming(false); });
  };
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(); } }}
      data-testid={`program-card-${program.id}`}
      className="bg-white border border-zinc-200 shadow-[3px_3px_0_0_#0A0A0B] hover:shadow-[5px_5px_0_0_#FFC60A] transition-shadow text-left flex flex-col cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#FFC60A]">
      <div className="px-3 py-2 bg-zinc-900 text-white border-b border-zinc-900 flex items-start gap-2">
        <FileSpreadsheet size={14} className="text-[#FFC60A] flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="font-display font-black text-base tracking-tighter truncate">{program.program_name}</div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-400 truncate">{program.client_name}</div>
        </div>
        <button onClick={onDeleteClick} disabled={deleting} data-testid={`program-delete-${program.id}`}
          title={confirming ? "Cliquez à nouveau pour confirmer la suppression" : "Supprimer"}
          className={`p-1 inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-widest ${
            confirming
              ? "bg-red-500 text-white border border-red-500 hover:bg-red-600"
              : "text-zinc-500 hover:text-red-400"
          }`}>
          {deleting ? <Loader2 size={12} className="animate-spin" />
            : confirming ? (<><Trash2 size={11} /> Confirmer</>)
            : <Trash2 size={12} />}
        </button>
      </div>
      <div className="p-3 grid grid-cols-3 gap-2 flex-1">
        <Stat label="Jours" value={program.blocks_count || 0} />
        <Stat label="Arrêts" value={program.stops_count || 0} />
        <Stat label="Événements" value={program.events_count || 0} />
      </div>
      <div className="px-3 py-1.5 border-t border-zinc-100 text-[10px] font-mono text-zinc-500 flex items-center justify-between">
        <span>{program.version ? `v ${program.version}` : "—"}</span>
        <span className="text-zinc-400">{program.source_filename || ""}</span>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="text-[9px] font-mono uppercase tracking-[0.25em] text-zinc-500">{label}</div>
      <div className="font-display font-black text-xl tracking-tighter leading-none mt-0.5">{value}</div>
    </div>
  );
}

/* ── Import wizard ─────────────────────────────────────────────────────── */
function ImportWizard({ wizard, setWizard, onDone }) {
  const inputRef = useRef(null);
  const onFile = async (f) => {
    if (!f) return;
    if (!/\.xlsx?$/i.test(f.name)) {
      toast.error("Format non supporté — uniquement .xlsx ou .xlsm");
      return;
    }
    setWizard({ ...wizard, file: f, busy: true });
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/programs/preview-excel-smart", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setWizard({ open: true, step: "preview", file: f, preview: data, busy: false });
      if (data?.template_applied) {
        toast.success("Template d'import existant appliqué automatiquement");
      } else if (data?.engine === "smart-v1" && (data?.confidence || 0) < 0.7) {
        toast.warning("Confiance d'analyse faible — vous pouvez lancer l'analyse IA");
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
      setWizard({ ...wizard, busy: false });
    }
  };
  const onAiAnalyze = async () => {
    if (!wizard.file) return;
    setWizard({ ...wizard, busy: true });
    try {
      const fd = new FormData();
      fd.append("file", wizard.file);
      const { data } = await api.post("/programs/preview-excel-smart/ai-analyze", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setWizard({ open: true, step: "preview", file: wizard.file, preview: data, busy: false });
      toast.success(`Analyse IA terminée (confiance ${Math.round((data.ai_assist?.confidence || 0) * 100)} %)`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
      setWizard({ ...wizard, busy: false });
    }
  };
  const onSaveTemplate = async () => {
    const p = wizard.preview;
    if (!p?.signature) { toast.error("Signature absente, impossible de sauver"); return; }
    const name = window.prompt("Nom du template d'import (ex. \"ASIABE Abeille\") :",
      p.client_name || wizard.file?.name?.replace(/\.xlsx?$/i, "") || "");
    if (!name) return;
    try {
      const mapping = p.sheets?.[0]?.mapping || p.mapping || {};
      const header_row = p.sheets?.[0]?.header_row ?? p.header_row ?? 0;
      await api.post("/programs/import-templates", {
        name, signature: p.signature, mapping, header_row,
        client_name: p.client_name || null,
      });
      toast.success(`Template « ${name} » sauvé — il sera appliqué automatiquement la prochaine fois`);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message); }
  };
  const onCommit = async () => {
    const p = wizard.preview;
    if (!p) return;
    setWizard({ ...wizard, busy: true });
    try {
      const isSmart = p.engine === "smart-v1";
      let res;
      if (isSmart) {
        // Normalise blocks for the smart endpoint
        const blocks = (p.blocks || []).map((b) => ({
          service_code: b.service_code,
          service_letter: b.service_letter || null,
          day_name: b.day_name,
          day_index: b.day_index,
          version: b.version || p.version || null,
          vehicle_type: b.vehicle_type || null,
          vehicle_capacity: b.vehicle_capacity || null,
          amplitude: b.amplitude || null,
          work_time: b.work_time || null,
          client_name: b.client_name_hint || p.client_name || null,
          direction_hint: b.direction_hint || null,
          stops: b.stops || [],
          events: b.events || [],
          warnings: b.warnings || [],
          // Carry over the auto-detection result so the backend can
          // create the durable assignment automatically.
          vehicle_match: b.vehicle_match || null,
        }));
        const uniq = Array.from(new Set(blocks.flatMap((b) => (b.stops || []).map((s) => s.name).filter(Boolean))));
        res = await api.post("/programs/import-excel-smart/confirm", {
          program_name: p.client_name || wizard.file?.name?.replace(/\.xlsx?$/i, "") || "Import",
          client_name: p.client_name || null,
          version: p.version || null,
          source_filename: wizard.file?.name,
          blocks,
          unique_stops: uniq,
          engine: p.engine,
          auto_generate: true,
        });
      } else {
        // legacy ASIABE path — also carries vehicle_match so the backend
        // can auto-create durable assignments just like the smart path.
        const legacyBlocks = (p.blocks || []).map((b) => ({
          ...b,
          vehicle_match: b.vehicle_match || null,
        }));
        res = await api.post("/programs/import-excel/confirm", {
          program_name: p.program_name,
          client_name: p.client_name || p.program_name,
          version: p.version,
          source_filename: wizard.file?.name,
          blocks: legacyBlocks,
          unique_stops: p.unique_stops,
          auto_generate: true,
        });
      }
      const gen = res?.data?.generation;
      if (gen?.ok) {
        toast.success(
          `Import OK — ${gen.persisted_count} services planifiés sur l'année ${gen.year}`,
          { duration: 4000 },
        );
      } else if (gen?.error) {
        toast.warning(`Import OK mais génération auto échouée : ${gen.error}`);
      }
      setWizard((w) => ({ ...w, committed: res?.data || {}, step: "assign", busy: false }));
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
      setWizard({ ...wizard, busy: false });
    }
  };
  const closeWizard = () => setWizard({ ...INITIAL_WIZARD });
  const stepIndex = WIZARD_STEPS.findIndex((s) => s.key === wizard.step);

  return (
    <div className="fixed inset-0 z-[1000] bg-black/60 flex items-center justify-center p-4" data-testid="programs-wizard">
      <div className="bg-white border-2 border-zinc-900 shadow-[6px_6px_0_0_#FFC60A] w-full max-w-5xl max-h-[92vh] flex flex-col">
        <header className="px-4 py-3 bg-zinc-900 text-white flex items-center gap-3 flex-shrink-0">
          <Upload size={16} className="text-[#FFC60A]" />
          <div className="flex-1">
            <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#FFC60A]">Wizard d&apos;import · Étape {stepIndex + 1}/6</div>
            <div className="font-display font-black text-lg tracking-tighter">
              {WIZARD_STEPS[stepIndex]?.label || "Import"}
            </div>
          </div>
          <button onClick={closeWizard} data-testid="wizard-close" className="text-zinc-400 hover:text-white"><X size={18} /></button>
        </header>

        <WizardStepper currentIdx={stepIndex} />

        {wizard.step === "drop" && (
          <div className="p-8 flex flex-col items-center justify-center flex-1">
            <button
              onClick={() => inputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); }}
              onDrop={(e) => { e.preventDefault(); onFile(e.dataTransfer?.files?.[0]); }}
              data-testid="wizard-dropzone"
              className="border-2 border-dashed border-zinc-300 hover:border-[#FFC60A] bg-zinc-50 hover:bg-yellow-50 transition-colors w-full max-w-2xl py-12 px-6 text-center cursor-pointer">
              <FileSpreadsheet size={48} className="mx-auto text-zinc-400 mb-3" />
              <div className="font-display font-black text-xl tracking-tighter mb-1">
                {wizard.busy ? "Analyse en cours…" : "Cliquez ou déposez votre fichier"}
              </div>
              <div className="text-sm text-zinc-500 mb-2">
                <strong>Tout fichier Excel scolaire</strong> au format .xlsx ou .xlsm
              </div>
              <div className="text-[10px] font-mono text-zinc-400 uppercase tracking-widest">
                Détection automatique · Formats ASIABE/MBC supportés en fast-path · IA en fallback
              </div>
              {wizard.busy && <Loader2 size={20} className="mx-auto mt-3 animate-spin text-zinc-700" />}
            </button>
            <input ref={inputRef} type="file" accept=".xlsx,.xlsm" hidden
              data-testid="wizard-file-input"
              onChange={(e) => onFile(e.target.files?.[0])} />
          </div>
        )}

        {wizard.step === "preview" && wizard.preview && (
          wizard.preview.engine === "epsp" ? (
            <EpspPreview wizard={wizard} setWizard={setWizard} onDone={onDone}
              onBack={() => setWizard({ ...wizard, step: "drop", preview: null })} />
          ) : (
            <WizardPreview preview={wizard.preview} file={wizard.file} busy={wizard.busy}
              onBack={() => setWizard({ ...wizard, step: "drop", preview: null })}
              onNext={() => setWizard({ ...wizard, step: "services" })}
              onAiAnalyze={onAiAnalyze}
              onSaveTemplate={onSaveTemplate} />
          )
        )}

        {wizard.step === "services" && wizard.preview && (
          <WizardServices preview={wizard.preview} busy={wizard.busy}
            onBack={() => setWizard({ ...wizard, step: "preview" })}
            onCommit={onCommit} />
        )}

        {wizard.step === "assign" && wizard.committed && (
          <WizardAssign wizard={wizard} setWizard={setWizard} />
        )}

        {wizard.step === "generate" && wizard.committed && (
          <WizardGenerate wizard={wizard} setWizard={setWizard} />
        )}

        {wizard.step === "done" && (
          <WizardDone wizard={wizard} onClose={onDone} />
        )}
      </div>
    </div>
  );
}

/* ── 6-step stepper bar ──────────────────────────────────────────────── */
function WizardStepper({ currentIdx }) {
  return (
    <ol className="flex items-stretch border-b border-zinc-200 bg-zinc-50 text-[10px] font-mono uppercase tracking-widest flex-shrink-0"
        data-testid="wizard-stepper">
      {WIZARD_STEPS.map((s, i) => {
        const done = i < currentIdx;
        const active = i === currentIdx;
        return (
          <li key={s.key}
              data-testid={`wizard-step-${s.key}`}
              data-active={active ? "1" : undefined}
              data-done={done ? "1" : undefined}
              className={`flex-1 px-3 py-2 flex items-center gap-2 border-r border-zinc-200 last:border-r-0 ${
                active ? "bg-[#FFC60A] text-zinc-900 font-bold"
                : done ? "bg-zinc-900 text-[#FFC60A]"
                : "bg-white text-zinc-400"
              }`}>
            <span className={`w-5 h-5 inline-flex items-center justify-center text-[10px] font-mono font-black ${
              active ? "bg-zinc-900 text-[#FFC60A]" : done ? "bg-[#FFC60A] text-zinc-900" : "bg-zinc-200 text-zinc-500"
            }`}>
              {done ? <CheckCircle2 size={12} /> : s.num}
            </span>
            <span className="truncate">{s.label}</span>
          </li>
        );
      })}
    </ol>
  );
}

function EpspPreview({ wizard, setWizard, onBack, onDone }) {
  const p = wizard.preview;
  const availableSheets = p.epsp_sheets_available || [];
  const [selected, setSelected] = useState(() => new Set(availableSheets));
  const [programName, setProgramName] = useState(
    p.program_name_hint || (wizard.file?.name || "").replace(/\.xlsx?$/i, "") || "EPSP"
  );
  const [schoolStart, setSchoolStart] = useState(p.school_start || "2026-08-17");
  const [schoolEnd, setSchoolEnd] = useState(p.school_end || "2027-07-02");
  const [dryRunReport, setDryRunReport] = useState(null);
  const [busy, setBusy] = useState(false);

  const toggle = (name) => setSelected((s) => {
    const next = new Set(s);
    if (next.has(name)) next.delete(name); else next.add(name);
    return next;
  });

  const fileToBase64 = (file) => new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(",", 2)[1]);
    r.onerror = reject;
    r.readAsDataURL(file);
  });

  const runImport = async (dryRun) => {
    if (!wizard.file) { toast.error("Fichier absent"); return; }
    if (selected.size === 0) { toast.error("Sélectionnez au moins une feuille"); return; }
    if (!programName.trim()) { toast.error("Nom du programme requis"); return; }
    setBusy(true);
    try {
      const b64 = await fileToBase64(wizard.file);
      const { data } = await api.post("/programs/import-epsp", {
        file_bytes_b64: b64,
        selected_sheets: Array.from(selected),
        program_name: programName.trim(),
        school_start: schoolStart,
        school_end: schoolEnd,
        source_filename: wizard.file.name,
        dry_run: dryRun,
      });
      if (dryRun) {
        setDryRunReport(data.report);
        toast.success(`Simulation OK — ${data.report?.planned_services_created || 0} services seraient créés`);
      } else {
        if (data.report?.no_change) {
          toast.info("Aucune modification détectée — fichier identique à la version active, rien n'a été recréé", { duration: 5000 });
        } else {
          toast.success(
            `Import EPSP réussi · ${data.report?.tours_created || 0} tournées · ${data.report?.planned_services_created || 0} services planifiés`,
            { duration: 5000 }
          );
        }
        setWizard({ ...wizard, committed: data, step: "done" });
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const perSheet = (p.tours || []).reduce((acc, t) => { acc[t.sheet] = t; return acc; }, {});
  const sum = p.summary || {};

  return (
    <>
      <div className="px-4 py-2 border-b bg-purple-50 border-purple-200 text-purple-900 flex items-center gap-3 text-xs" data-testid="epsp-banner">
        <CheckCircle2 size={14} />
        <div className="font-bold">Format EPSP détecté</div>
        <span className="text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 border border-purple-300 bg-purple-100">
          epsp · confiance {Math.round((p.confidence || 0.95) * 100)} %
        </span>
        <span className="ml-auto text-[10px] font-mono text-purple-700 truncate max-w-[280px]">{wizard.file?.name}</span>
      </div>

      <div className="px-4 py-3 border-b border-zinc-200 bg-zinc-50 flex items-center gap-4 flex-wrap flex-shrink-0" data-testid="epsp-kpis">
        <KpiTile label="Feuilles" value={sum.sheets_available || 0} testId="epsp-kpi-sheets" />
        <KpiTile label="Tournées" value={sum.tours || 0} testId="epsp-kpi-tours" />
        <KpiTile label="Variantes" value={sum.variants || 0} testId="epsp-kpi-variants" />
        <KpiTile label="Arrêts" value={sum.stops || 0} testId="epsp-kpi-stops" />
        <KpiTile label="Événements" value={sum.events || 0} testId="epsp-kpi-events" />
        <KpiTile label="Waypoints" value={sum.waypoints || 0} testId="epsp-kpi-waypoints" />
        <KpiTile label="Arrêts uniques" value={sum.unique_stops || 0} testId="epsp-kpi-unique-stops" />
      </div>

      <div className="p-4 grid grid-cols-1 lg:grid-cols-3 gap-4 flex-shrink-0 border-b border-zinc-200">
        <div>
          <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1 block">Nom du programme</label>
          <input value={programName} onChange={(e) => setProgramName(e.target.value)}
            className="w-full border border-zinc-900 px-3 py-2 text-sm"
            data-testid="epsp-program-name" />
        </div>
        <div>
          <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1 block">Début année scolaire</label>
          <input type="date" value={schoolStart} onChange={(e) => setSchoolStart(e.target.value)}
            className="w-full border border-zinc-900 px-3 py-2 text-sm"
            data-testid="epsp-school-start" />
        </div>
        <div>
          <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1 block">Fin année scolaire</label>
          <input type="date" value={schoolEnd} onChange={(e) => setSchoolEnd(e.target.value)}
            className="w-full border border-zinc-900 px-3 py-2 text-sm"
            data-testid="epsp-school-end" />
        </div>
      </div>

      <div className="flex-1 overflow-auto p-4">
        <div className="mb-3 flex items-center gap-2">
          <h3 className="font-display font-black uppercase text-sm tracking-tight">
            Feuilles à importer ({selected.size} / {availableSheets.length})
          </h3>
          <button onClick={() => setSelected(new Set(availableSheets))}
            className="ml-auto text-[10px] font-mono uppercase tracking-widest px-2 py-1 border border-zinc-300 hover:bg-zinc-100"
            data-testid="epsp-select-all">
            Tout cocher
          </button>
          <button onClick={() => setSelected(new Set())}
            className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 border border-zinc-300 hover:bg-zinc-100"
            data-testid="epsp-select-none">
            Décocher
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="epsp-sheets-grid">
          {availableSheets.map((name) => {
            const t = perSheet[name];
            const totals = t?.totals || {};
            const on = selected.has(name);
            return (
              <label key={name}
                className={`border p-3 cursor-pointer transition-colors ${on ? "border-purple-600 bg-purple-50 shadow-[3px_3px_0_0_#A855F7]" : "border-zinc-300 hover:border-zinc-900"}`}
                data-testid={`epsp-sheet-${name}`}>
                <div className="flex items-center gap-2">
                  <input type="checkbox" checked={on} onChange={() => toggle(name)}
                    data-testid={`epsp-sheet-checkbox-${name}`} className="accent-purple-600" />
                  <span className="font-display font-black uppercase tracking-tight text-lg">{name}</span>
                  <span className="ml-auto text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                    {totals.variants || 0} variante{(totals.variants || 0) > 1 ? "s" : ""}
                  </span>
                </div>
                <div className="mt-2 flex gap-3 text-[11px] font-mono text-zinc-600">
                  <span><b>{totals.stops || 0}</b> stops</span>
                  <span><b>{totals.events || 0}</b> events</span>
                  <span><b>{totals.waypoints || 0}</b> wpts</span>
                </div>
                {t?.unknown_activities && Object.keys(t.unknown_activities).length > 0 && (
                  <div className="mt-2 text-[10px] text-amber-700 font-mono">
                    ⚠ {Object.entries(t.unknown_activities).slice(0, 2).map(([k, v]) => `${k}×${v}`).join(", ")}
                  </div>
                )}
              </label>
            );
          })}
        </div>

        {dryRunReport && (
          <div className="mt-4 p-3 border-2 border-purple-600 bg-purple-50" data-testid="epsp-dryrun-report">
            <div className="font-display font-black uppercase text-sm tracking-tight text-purple-900 mb-2">
              Résultat simulation
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
              <Mini label="Tournées" value={dryRunReport.tours_created} />
              <Mini label="Variantes" value={dryRunReport.template_days_created} />
              <Mini label="Services planifiés" value={dryRunReport.planned_services_created} />
              <Mini label="Affectations auto" value={dryRunReport.durable_assignments_created} />
            </div>
            {(dryRunReport.pending_assignment_tours || []).length > 0 && (
              <div className="mt-2 text-[11px] text-amber-800">
                Affectation manuelle requise : {(dryRunReport.pending_assignment_tours || []).join(", ")}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="border-t border-zinc-200 p-3 flex items-center gap-2 bg-zinc-50">
        <button onClick={onBack} disabled={busy}
          className="px-3 py-1.5 border border-zinc-300 hover:bg-white text-[11px] font-bold uppercase tracking-widest">
          Retour
        </button>
        <div className="flex-1" />
        <button onClick={() => runImport(true)} disabled={busy || selected.size === 0}
          data-testid="epsp-dryrun"
          className="px-3 py-1.5 border border-zinc-900 hover:bg-zinc-100 text-[11px] font-black uppercase tracking-widest inline-flex items-center gap-1.5 disabled:opacity-50">
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Eye size={12} />}
          Simuler
        </button>
        <button onClick={() => runImport(false)} disabled={busy || selected.size === 0}
          data-testid="epsp-commit"
          className="px-4 py-1.5 bg-purple-600 text-white border border-purple-800 hover:bg-purple-700 text-[11px] font-black uppercase tracking-widest inline-flex items-center gap-1.5 disabled:opacity-50">
          {busy ? <Loader2 size={12} className="animate-spin" /> : <ArrowRight size={12} />}
          Importer {selected.size} feuille{selected.size > 1 ? "s" : ""}
        </button>
      </div>
    </>
  );
}

function WizardPreview({ preview, file, busy, onBack, onNext, onAiAnalyze, onSaveTemplate }) {
  const blocks = preview.blocks || [];
  const [expanded, setExpanded] = useState(0);
  const isSmart = preview.engine === "smart-v1";
  const confidence = preview.confidence ?? null;
  const confidencePct = confidence === null ? null : Math.round(confidence * 100);
  const confidenceTone = confidencePct === null ? "info" : (confidencePct >= 70 ? "ok" : confidencePct >= 40 ? "warn" : "err");
  const aiAvailable = isSmart && confidence !== null && confidence < 0.7;
  const sum = preview.summary || {};
  // Normalised counters (works for both legacy + smart payloads)
  const toursCount = sum.tours_detected ?? sum.blocks_count ?? blocks.length;
  const stopsCount = sum.stops_detected ?? sum.stops_count ?? preview.unique_stops?.length ?? 0;
  const daysCount = sum.days_detected ?? new Set(blocks.map((b) => b.day_index)).size;
  const eventsCount = sum.events_detected ?? sum.events_count ?? 0;
  const errorsCount = sum.errors_detected ?? (preview.warnings?.length || 0);

  return (
    <>
      {/* Confidence banner */}
      <div className={`px-4 py-2 border-b flex items-center gap-3 text-xs flex-wrap ${
        confidenceTone === "ok" ? "bg-emerald-50 border-emerald-200 text-emerald-900" :
        confidenceTone === "warn" ? "bg-amber-50 border-amber-200 text-amber-900" :
        confidenceTone === "err" ? "bg-red-50 border-red-200 text-red-900" :
        "bg-zinc-50 border-zinc-200 text-zinc-700"
      }`} data-testid="wizard-confidence-banner">
        <div className="flex items-center gap-1.5 font-bold">
          {confidenceTone === "ok" ? <CheckCircle2 size={14} /> :
           confidenceTone === "err" ? <AlertCircle size={14} /> :
           <AlertTriangle size={14} />}
          {isSmart ? "Détection automatique" : "Format reconnu (fast-path)"}
          {confidencePct !== null && <span className="font-mono">· confiance {confidencePct} %</span>}
        </div>
        <span className="text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 border" data-testid="wizard-engine-badge">
          {preview.engine || "legacy"}
        </span>
        {preview.template_applied && (
          <span className="text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 border border-sky-300 bg-sky-50 text-sky-700 inline-flex items-center gap-1" data-testid="wizard-template-applied">
            <ShieldCheck size={11} /> Template appliqué
          </span>
        )}
        <div className="flex-1" />
        {aiAvailable && (
          <button onClick={onAiAnalyze} disabled={busy} data-testid="wizard-ai-analyze"
            className="px-3 py-1 bg-purple-600 text-white border border-purple-600 hover:bg-purple-700 text-[10px] font-black uppercase tracking-widest inline-flex items-center gap-1.5 disabled:opacity-50">
            {busy ? <Loader2 size={11} className="animate-spin" /> : <Brain size={11} />} Analyser avec IA
          </button>
        )}
        {preview.signature && !preview.template_applied && (
          <button onClick={onSaveTemplate} disabled={busy} data-testid="wizard-save-template"
            className="px-3 py-1 border border-zinc-300 hover:border-zinc-900 hover:bg-zinc-100 text-[10px] font-black uppercase tracking-widest inline-flex items-center gap-1.5">
            <BookmarkPlus size={11} /> Sauver comme template
          </button>
        )}
      </div>

      {/* Detection KPIs */}
      <div className="px-4 py-3 border-b border-zinc-200 bg-zinc-50 flex items-center gap-4 flex-wrap flex-shrink-0" data-testid="wizard-kpis">
        <Mini label="Client" value={preview.client_name || preview.program_name || "—"} />
        <div className="h-8 w-px bg-zinc-300" />
        <KpiTile label="Tournées" value={toursCount} testId="wizard-kpi-tours" />
        <KpiTile label="Arrêts" value={stopsCount} testId="wizard-kpi-stops" />
        <KpiTile label="Jours" value={daysCount} testId="wizard-kpi-days" />
        <KpiTile label="Événements" value={eventsCount} testId="wizard-kpi-events" />
        <KpiTile label="Erreurs" value={errorsCount} tone={errorsCount ? "warn" : null} testId="wizard-kpi-errors" />
        <div className="flex-1" />
        <div className="text-[10px] font-mono text-zinc-500 truncate max-w-[260px]">{file?.name}</div>
      </div>

      {/* Block-level warnings recap */}
      {(blocks.some((b) => (b.warnings || []).length > 0) || (preview.warnings || []).length > 0) && (
        <div className="px-4 py-2 bg-amber-50 border-b border-amber-200 text-xs text-amber-900" data-testid="wizard-warnings">
          <div className="font-bold mb-0.5 inline-flex items-center gap-1"><AlertCircle size={12} /> Avertissements</div>
          <ul className="list-disc list-inside text-[11px] max-h-24 overflow-y-auto">
            {(preview.warnings || []).map((w, i) => <li key={`p${i}`}>{w}</li>)}
            {blocks.flatMap((b, bi) => (b.warnings || []).map((w, wi) => (
              <li key={`b${bi}-${wi}`}>Bloc {bi + 1} ({b.service_code || "?"}{b.service_letter || ""} {b.day_name || ""}): {w}</li>
            )))}
          </ul>
        </div>
      )}

      {/* Vehicle auto-detection preview table */}
      {blocks.some((b) => b.vehicle_match) && (
        <VehicleMatchPanel blocks={blocks} summary={preview.vehicle_match_summary} />
      )}

      <div className="flex-1 overflow-y-auto" data-testid="wizard-blocks">
        {blocks.length === 0 ? (
          <div className="p-8 text-center text-zinc-400" data-testid="wizard-no-blocks">
            <AlertCircle size={32} className="mx-auto mb-2 text-zinc-300" />
            Aucun bloc détecté dans le fichier.
            {aiAvailable && <div className="mt-3 text-xs">Cliquez sur <strong>Analyser avec IA</strong> pour tenter une détection avancée.</div>}
          </div>
        ) : (
          <div className="divide-y divide-zinc-200">
            {blocks.map((b, i) => (
              <BlockPreview key={i} block={b} index={i} expanded={expanded === i} onToggle={() => setExpanded(expanded === i ? -1 : i)} />
            ))}
          </div>
        )}
      </div>

      <div className="px-4 py-3 border-t border-zinc-200 bg-zinc-50 flex items-center gap-2 flex-shrink-0">
        <button onClick={onBack} disabled={busy} data-testid="wizard-back"
          className="px-3 py-1.5 border border-zinc-300 hover:bg-white text-[11px] font-bold uppercase tracking-widest">
          Retour
        </button>
        <div className="flex-1" />
        <span className="text-[10px] font-mono text-zinc-500">
          {toursCount} tournée{toursCount > 1 ? "s" : ""} · {stopsCount} arrêts
        </span>
        <button onClick={onNext} disabled={busy || blocks.length === 0} data-testid="wizard-confirm"
          className="px-4 py-1.5 bg-zinc-900 text-white border border-zinc-900 hover:bg-[#FFC60A] hover:text-zinc-900 text-[11px] font-black uppercase tracking-widest inline-flex items-center gap-1.5 disabled:opacity-50">
          {busy ? <Loader2 size={12} className="animate-spin" /> : <ArrowRight size={12} />} Continuer
        </button>
      </div>
    </>
  );
}

/* ── Step 3: Services — show planned services that will be created ──── */
function WizardServices({ preview, busy, onBack, onCommit }) {
  const blocks = preview.blocks || [];
  // Derive per-day counts client-side from the detected blocks. A "service"
  // here is one block = one day-pattern. The actual yearly persistence
  // happens server-side at commit time (with auto_generate=true).
  const byDay = useMemo(() => {
    const m = {};
    blocks.forEach((b) => {
      const k = b.day_index != null ? DAY_NAMES[b.day_index] : (b.day_name || "?");
      m[k] = (m[k] || 0) + 1;
    });
    return m;
  }, [blocks]);
  const byCode = useMemo(() => {
    const m = {};
    blocks.forEach((b) => {
      const k = `${b.service_code || "?"}${b.service_letter || ""}`;
      m[k] = (m[k] || 0) + 1;
    });
    return m;
  }, [blocks]);
  // Estimate yearly persistence: 1 block × ~40 school weeks = ~40 services / block.
  // Real number is server-side. This is just for the user's mental model.
  const estimateYear = blocks.length * 40;

  return (
    <>
      <div className="px-4 py-3 border-b border-zinc-200 bg-zinc-50 text-sm" data-testid="wizard-services-summary">
        <div className="font-display font-black text-base tracking-tighter mb-1 inline-flex items-center gap-2">
          <Layers size={14} className="text-zinc-500" />
          Services qui seront créés
        </div>
        <div className="text-xs text-zinc-600">
          <strong>{blocks.length}</strong> tournée{blocks.length > 1 ? "s" : ""} hebdomadaire{blocks.length > 1 ? "s" : ""}.
          Au total, <strong>≈{estimateYear}</strong> services planifiés sur l&apos;année scolaire
          (Lundi → Vendredi, hors vacances).
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-4 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="wizard-services-body">
        <BreakdownCard title="Par jour de semaine" data={byDay} mapper={(k) => k} testId="wizard-services-by-day" />
        <BreakdownCard title="Par code service" data={byCode} mapper={(k) => `Service ${k}`} testId="wizard-services-by-code" />
        <div className="md:col-span-2 border border-zinc-200 bg-white" data-testid="wizard-services-table">
          <div className="px-2 py-1.5 bg-zinc-50 border-b border-zinc-200 text-[10px] font-mono uppercase tracking-widest font-bold">
            Détail des tournées détectées ({blocks.length})
          </div>
          <table className="w-full text-xs">
            <thead className="bg-zinc-50 border-b border-zinc-200">
              <tr className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                <th className="text-left px-2 py-1.5">Jour</th>
                <th className="text-left px-2 py-1.5">Code</th>
                <th className="text-left px-2 py-1.5">Véhicule détecté</th>
                <th className="text-left px-2 py-1.5">Arrêts</th>
                <th className="text-left px-2 py-1.5">Amplitude</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {blocks.map((b, i) => (
                <tr key={i} data-testid={`wizard-services-row-${i}`}>
                  <td className="px-2 py-1.5 font-bold">{b.day_name || DAY_NAMES[b.day_index] || "?"}</td>
                  <td className="px-2 py-1.5">
                    <span className="font-mono font-bold px-1.5 py-0.5 bg-zinc-900 text-[#FFC60A]">
                      {b.service_code || "?"}{b.service_letter || ""}
                    </span>
                  </td>
                  <td className="px-2 py-1.5 text-zinc-600">
                    {b.vehicle_match?.status === "exact" ? (
                      <span className="inline-flex items-center gap-1 text-emerald-700">
                        <CheckCircle2 size={11} /> {b.vehicle_match.matches?.[0]?.label || b.vehicle_match.matches?.[0]?.model}
                      </span>
                    ) : b.vehicle_match?.status === "multiple" ? (
                      <span className="text-amber-700">{b.vehicle_match.matches?.length} candidats</span>
                    ) : (
                      <span className="text-zinc-400 italic">Manuel</span>
                    )}
                  </td>
                  <td className="px-2 py-1.5 font-mono text-zinc-500">{(b.stops || []).length}</td>
                  <td className="px-2 py-1.5 font-mono text-zinc-500">{b.amplitude || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <footer className="px-4 py-3 border-t border-zinc-200 bg-zinc-50 flex items-center gap-2 flex-shrink-0">
        <button onClick={onBack} disabled={busy} data-testid="wizard-services-back"
          className="px-3 py-1.5 border border-zinc-300 hover:bg-white text-[11px] font-bold uppercase tracking-widest inline-flex items-center gap-1">
          <ArrowLeft size={12} /> Retour
        </button>
        <div className="flex-1" />
        <span className="text-[10px] font-mono text-zinc-500">
          Cliquez pour importer puis affecter
        </span>
        <button onClick={onCommit} disabled={busy || blocks.length === 0}
          data-testid="wizard-services-commit"
          className="px-4 py-1.5 bg-zinc-900 text-white border border-zinc-900 hover:bg-[#FFC60A] hover:text-zinc-900 text-[11px] font-black uppercase tracking-widest inline-flex items-center gap-1.5 disabled:opacity-50">
          {busy ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />}
          Importer et continuer
        </button>
      </footer>
    </>
  );
}

/* ── Step 4: Affecter — manual assignment of unmatched tours ───────────── */
function WizardAssign({ wizard, setWizard }) {
  const committed = wizard.committed || {};
  const autoAssigned = committed.auto_assigned || [];
  // "skipped" includes both "Affectation durable existante" (already OK)
  // and "Aucun véhicule reconnu" / "Plusieurs véhicules possibles" (manual).
  const skipped = committed.skipped_assignments || [];
  const manual = skipped.filter((s) => s.reason !== "Affectation durable existante");
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [drafts, setDrafts] = useState({});  // tour_id -> {vehicle_id, driver_id}
  const [saving, setSaving] = useState({});  // tour_id -> bool
  const [savedIds, setSavedIds] = useState(new Set());  // tour_id set

  useEffect(() => {
    Promise.all([
      api.get("/vehicles").catch(() => ({ data: [] })),
      api.get("/users?role=driver").catch(() => ({ data: [] })),
    ]).then(([v, d]) => {
      setVehicles(v.data?.items || v.data || []);
      setDrivers(d.data?.items || d.data || []);
    });
  }, []);

  const saveAssignment = async (tourId) => {
    const cur = drafts[tourId] || {};
    if (!cur.vehicle_id) { toast.error("Sélectionnez un véhicule"); return; }
    setSaving((s) => ({ ...s, [tourId]: true }));
    try {
      const today = new Date().toISOString().slice(0, 10);
      await api.post("/tour-assignments", {
        tour_id: tourId,
        vehicle_id: cur.vehicle_id,
        driver_id: cur.driver_id || null,
        kind: "durable",
        start_date: today,
        end_date: null,
        notes: "Affecté via Wizard d'import",
      });
      toast.success("Affectation enregistrée");
      setSavedIds((s) => new Set([...s, tourId]));
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally {
      setSaving((s) => ({ ...s, [tourId]: false }));
    }
  };

  const remaining = manual.filter((m) => !savedIds.has(m.tour_id));
  const canContinue = true; // Continuing without assigning is OK; missions stay pending

  return (
    <>
      <div className="px-4 py-3 border-b border-zinc-200 bg-zinc-50 text-sm" data-testid="wizard-assign-summary">
        <div className="font-display font-black text-base tracking-tighter mb-1 inline-flex items-center gap-2">
          <UserIcon size={14} className="text-zinc-500" /> Affecter bus &amp; chauffeur
        </div>
        <div className="text-xs text-zinc-600 flex items-center gap-4 flex-wrap">
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-emerald-100 text-emerald-800 border border-emerald-300 font-mono">
            <CheckCircle2 size={11} /> {autoAssigned.length} affectée{autoAssigned.length > 1 ? "s" : ""} automatiquement
          </span>
          {remaining.length > 0 ? (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-amber-100 text-amber-900 border border-amber-300 font-mono">
              <AlertTriangle size={11} /> {remaining.length} à affecter manuellement
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-emerald-100 text-emerald-800 border border-emerald-300 font-mono">
              <CheckCircle2 size={11} /> Toutes affectées
            </span>
          )}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-4" data-testid="wizard-assign-body">
        {autoAssigned.length > 0 && (
          <div className="border border-emerald-200 bg-emerald-50" data-testid="wizard-assign-auto">
            <div className="px-3 py-2 bg-emerald-100 border-b border-emerald-200 text-[10px] font-mono uppercase tracking-widest text-emerald-900 font-bold">
              Affectations automatiques ({autoAssigned.length})
            </div>
            <ul className="divide-y divide-emerald-100 text-sm">
              {autoAssigned.map((a) => (
                <li key={a.assignment_id || a.tour_id} className="px-3 py-1.5 flex items-center gap-2"
                    data-testid={`wizard-assign-auto-${a.tour_id}`}>
                  <CheckCircle2 size={12} className="text-emerald-600 shrink-0" />
                  <span className="font-mono font-bold">{a.tour_label}</span>
                  <span className="text-zinc-500">→</span>
                  <Bus size={11} className="text-zinc-400" />
                  <span>{a.vehicle_label || a.vehicle_id?.slice(0, 8)}</span>
                  <span className="ml-auto text-[10px] font-mono text-emerald-700 uppercase tracking-widest">OK</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        {manual.length > 0 ? (
          <div className="border border-amber-200 bg-amber-50/40">
            <div className="px-3 py-2 bg-amber-100 border-b border-amber-200 text-[10px] font-mono uppercase tracking-widest text-amber-900 font-bold flex items-center gap-2">
              <AlertTriangle size={11} /> Tournées à affecter ({remaining.length}/{manual.length})
            </div>
            <ul className="divide-y divide-amber-100">
              {manual.map((m) => {
                const saved = savedIds.has(m.tour_id);
                const draft = drafts[m.tour_id] || {};
                return (
                  <li key={m.tour_id} className={`px-3 py-2 ${saved ? "bg-emerald-50/60" : ""}`}
                      data-testid={`wizard-assign-manual-${m.tour_id}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-mono font-bold text-sm">{m.tour_label}</span>
                      <span className="text-[10px] text-zinc-500 italic">{m.reason}</span>
                      {saved && <span className="ml-auto inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-emerald-700 font-bold"><CheckCircle2 size={11} /> Enregistré</span>}
                    </div>
                    {!saved && (
                      <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-2">
                        <select
                          value={draft.vehicle_id || ""}
                          onChange={(e) => setDrafts({ ...drafts, [m.tour_id]: { ...draft, vehicle_id: e.target.value } })}
                          data-testid={`wizard-assign-vehicle-${m.tour_id}`}
                          className="border border-zinc-300 px-2 py-1 text-sm bg-white">
                          <option value="">— Bus —</option>
                          {vehicles.map((v) => {
                            const name = v.model || v.label || v.plate || v.license_plate || (v.id || "").slice(0, 8);
                            return <option key={v.id} value={v.id}>{name}</option>;
                          })}
                        </select>
                        <select
                          value={draft.driver_id || ""}
                          onChange={(e) => setDrafts({ ...drafts, [m.tour_id]: { ...draft, driver_id: e.target.value } })}
                          data-testid={`wizard-assign-driver-${m.tour_id}`}
                          className="border border-zinc-300 px-2 py-1 text-sm bg-white">
                          <option value="">— Chauffeur (option.) —</option>
                          {drivers.map((d) => <option key={d.id} value={d.id}>{d.name || d.email}</option>)}
                        </select>
                        <button
                          onClick={() => saveAssignment(m.tour_id)}
                          disabled={saving[m.tour_id] || !draft.vehicle_id}
                          data-testid={`wizard-assign-save-${m.tour_id}`}
                          className="px-3 py-1 bg-zinc-900 text-white border border-zinc-900 hover:bg-[#FFC60A] hover:text-zinc-900 text-[10px] font-black uppercase tracking-widest inline-flex items-center justify-center gap-1 disabled:opacity-50">
                          {saving[m.tour_id] ? <Loader2 size={11} className="animate-spin" /> : <CheckCircle2 size={11} />}
                          Affecter
                        </button>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        ) : (
          <div className="border border-zinc-200 bg-white p-6 text-center text-zinc-500 text-sm" data-testid="wizard-assign-empty">
            <CheckCircle2 size={28} className="mx-auto mb-2 text-emerald-500" />
            Toutes les tournées sont affectées. Vous pouvez passer à la génération des missions.
          </div>
        )}
      </div>
      <footer className="px-4 py-3 border-t border-zinc-200 bg-zinc-50 flex items-center gap-2 flex-shrink-0">
        <button onClick={() => setWizard({ ...wizard, step: "services" })} data-testid="wizard-assign-back"
          className="px-3 py-1.5 border border-zinc-300 hover:bg-white text-[11px] font-bold uppercase tracking-widest inline-flex items-center gap-1">
          <ArrowLeft size={12} /> Retour
        </button>
        <div className="flex-1" />
        <span className="text-[10px] font-mono text-zinc-500">
          {remaining.length > 0
            ? `${remaining.length} tournée${remaining.length > 1 ? "s" : ""} non affectée${remaining.length > 1 ? "s" : ""} — les missions resteront « À affecter »`
            : "Tout est prêt"}
        </span>
        <button
          onClick={() => setWizard({ ...wizard, step: "generate" })}
          disabled={!canContinue}
          data-testid="wizard-assign-next"
          className="px-4 py-1.5 bg-zinc-900 text-white border border-zinc-900 hover:bg-[#FFC60A] hover:text-zinc-900 text-[11px] font-black uppercase tracking-widest inline-flex items-center gap-1.5 disabled:opacity-50">
          <ArrowRight size={12} /> Continuer
        </button>
      </footer>
    </>
  );
}

/* ── Step 5: Générer — kick off mission generation ────────────────────── */
function WizardGenerate({ wizard, setWizard }) {
  const [windowDays, setWindowDays] = useState(30);
  const [busy, setBusy] = useState(false);

  const today = new Date();
  const fromIso = today.toISOString().slice(0, 10);
  const to = new Date(today.getTime() + windowDays * 86400000);
  const toIso = to.toISOString().slice(0, 10);

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/missions/generate", { from: fromIso, to: toIso });
      toast.success(`${data.created || 0} mission(s) créée(s) · ${data.pending || 0} en attente d'affectation`,
                    { duration: 6000 });
      setWizard({ ...wizard, generation: data, step: "done" });
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <>
      <div className="flex-1 overflow-y-auto p-6" data-testid="wizard-generate-body">
        <div className="max-w-2xl mx-auto">
          <div className="mb-4 inline-flex items-center gap-2">
            <Wand2 size={16} className="text-zinc-700" />
            <h3 className="font-display font-black text-xl tracking-tighter">Générer les missions</h3>
          </div>
          <p className="text-sm text-zinc-600 mb-5">
            LOGIBUS va matérialiser <strong>une mission par jour ouvré × tournée affectée</strong>
            {" "}sur la fenêtre choisie. Les tournées sans affectation resteront en statut <em>« À affecter »</em>.
          </p>
          <div className="grid grid-cols-3 gap-2 mb-5" data-testid="wizard-generate-window">
            {[7, 14, 30].map((d) => (
              <button
                key={d}
                onClick={() => setWindowDays(d)}
                data-testid={`wizard-generate-window-${d}`}
                className={`border p-3 text-center transition-colors ${
                  windowDays === d
                    ? "border-zinc-900 bg-[#FFC60A] text-zinc-900 shadow-[3px_3px_0_0_#0A0A0B]"
                    : "border-zinc-300 bg-white hover:border-zinc-900"
                }`}>
                <div className="font-display font-black text-3xl tracking-tighter">{d}j</div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mt-0.5">
                  {d === 7 ? "Semaine" : d === 14 ? "2 semaines" : "30 jours"}
                </div>
              </button>
            ))}
          </div>
          <div className="border border-zinc-200 bg-zinc-50 px-3 py-2 text-xs font-mono text-zinc-700 mb-5">
            Période : <strong>{fromIso}</strong> → <strong>{toIso}</strong>
          </div>
          <div className="text-[11px] text-zinc-500 border-l-2 border-zinc-300 pl-3 mb-2">
            ⓘ Vous pouvez aussi générer plus tard via le bouton <strong>Générer missions (30j)</strong> sur la page
            <em> Affectations flotte</em>. Un cron automatique crée également les missions du lendemain chaque nuit à 00:05.
          </div>
        </div>
      </div>
      <footer className="px-4 py-3 border-t border-zinc-200 bg-zinc-50 flex items-center gap-2 flex-shrink-0">
        <button onClick={() => setWizard({ ...wizard, step: "assign" })} disabled={busy}
          data-testid="wizard-generate-back"
          className="px-3 py-1.5 border border-zinc-300 hover:bg-white text-[11px] font-bold uppercase tracking-widest inline-flex items-center gap-1">
          <ArrowLeft size={12} /> Retour
        </button>
        <button
          onClick={() => setWizard({ ...wizard, step: "done" })}
          disabled={busy}
          data-testid="wizard-generate-skip"
          className="px-3 py-1.5 border border-zinc-300 hover:bg-white text-[10px] font-mono uppercase tracking-widest text-zinc-500 hover:text-zinc-900">
          Passer cette étape
        </button>
        <div className="flex-1" />
        <button
          onClick={generate}
          disabled={busy}
          data-testid="wizard-generate-run"
          className="px-4 py-1.5 bg-zinc-900 text-white border border-zinc-900 hover:bg-[#FFC60A] hover:text-zinc-900 text-[11px] font-black uppercase tracking-widest inline-flex items-center gap-1.5 disabled:opacity-50">
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Zap size={12} />}
          Générer {windowDays}j de missions
        </button>
      </footer>
    </>
  );
}

/* ── Step 6: Done — final confirmation summary ─────────────────────────── */
function WizardDone({ wizard, onClose }) {
  const c = wizard.committed || {};
  const g = wizard.generation || {};
  const gen = c.generation || {};
  // EPSP commits store their counters under `report`; legacy stores them
  // flat on `c`. Coalesce both so the confirmation tiles work in both flows.
  const rep = c.report || {};
  const toursValue      = rep.tours_created ?? c.blocks_count ?? c.template?.blocks_count ?? "—";
  const servicesValue   = rep.planned_services_created ?? gen.persisted_count ?? "—";
  const autoValue       = rep.durable_assignments_created ?? (c.auto_assigned || []).length ?? 0;
  const missionsValue   = rep.missions_created ?? g.created ?? 0;
  // Point 5 MBC : ne jamais afficher "Import terminé" si aucune course
  // n'a été créée — c'est un échec qui doit être explicite.
  const servicesNum = Number(servicesValue);
  const importFailed = !rep.no_change && !Number.isNaN(servicesNum) && servicesNum === 0;
  const untransferred = rep.assignments_untransferred || [];
  return (
    <>
      <div className="flex-1 overflow-y-auto p-6" data-testid="wizard-done-body">
        {rep.no_change && (
          <div className="max-w-2xl mx-auto mb-4 bg-sky-50 border border-sky-300 px-4 py-3 text-sm" data-testid="wizard-done-no-change">
            ℹ️ <strong>Aucune modification détectée</strong> — {rep.message}
          </div>
        )}
        {rep.replaced_template_id && (
          <div className="max-w-2xl mx-auto mb-4 bg-emerald-50 border border-emerald-300 px-4 py-3 text-sm" data-testid="wizard-done-replaced">
            🔄 <strong>Programme remplacé</strong> — la version {rep.replaced_version || 1} a été archivée dans l&apos;historique ; la version {rep.version} est désormais la seule active dans le planning.
          </div>
        )}
        {rep.similar_program && (
          <div className="max-w-2xl mx-auto mb-4 bg-amber-50 border border-amber-300 px-4 py-3 text-sm" data-testid="wizard-done-similar">
            ⚠️ <strong>Programme similaire détecté</strong> — « {rep.similar_program.program_name} » partage {Math.round(rep.similar_program.service_code_overlap * 100)}% des tournées. Ce fichier a été importé comme <strong>nouveau programme</strong>. Si c&apos;était une mise à jour, supprimez le doublon ou ré-importez avec le même nom de programme.
          </div>
        )}
        {untransferred.length > 0 && (
          <div className="max-w-2xl mx-auto mb-4 bg-red-50 border border-red-300 px-4 py-3 text-sm" data-testid="wizard-done-untransferred">
            ⚠️ <strong>{untransferred.length} affectation(s) non transférée(s)</strong> (tournée absente ou ambiguë dans la nouvelle version) : {untransferred.map((u) => u.service_code || "?").join(", ")}. Les tournées concernées sont dans le pool « À affecter ».
          </div>
        )}
        {(rep.assignments_transferred || []).length > 0 && (
          <div className="max-w-2xl mx-auto mb-4 bg-emerald-50 border border-emerald-300 px-4 py-2 text-sm" data-testid="wizard-done-transferred">
            ✅ <strong>{rep.assignments_transferred.length} affectation(s) transférée(s)</strong> automatiquement vers la nouvelle version : {rep.assignments_transferred.map((t) => t.service_code || "?").join(", ")}
          </div>
        )}
        <div className="max-w-2xl mx-auto text-center">
          {importFailed ? (
            <>
              <div className="w-16 h-16 mx-auto bg-red-100 border-2 border-red-500 inline-flex items-center justify-center mb-3">
                <AlertTriangle size={32} className="text-red-600" />
              </div>
              <h3 className="font-display font-black text-3xl tracking-tighter text-red-700" data-testid="wizard-done-failed-title">Import incomplet</h3>
              <p className="text-sm text-zinc-600 mt-1 mb-6">
                Le fichier a été lu mais <strong>aucune course n&apos;a été créée</strong>.
                Vérifiez le calendrier scolaire (dates de début/fin), les jours de
                circulation et les semaines A/B du fichier.
              </p>
            </>
          ) : (
            <>
              <div className="w-16 h-16 mx-auto bg-emerald-100 border-2 border-emerald-500 inline-flex items-center justify-center mb-3">
                <CheckCircle2 size={32} className="text-emerald-600" />
              </div>
              <h3 className="font-display font-black text-3xl tracking-tighter">Import terminé</h3>
              <p className="text-sm text-zinc-600 mt-1 mb-6">Voici le récapitulatif de l&apos;opération.</p>
            </>
          )}
        </div>
        <div className="max-w-2xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-2 mb-5" data-testid="wizard-done-kpis">
          <DoneStat label="Tournées" value={toursValue} testId="wizard-done-tours" />
          <DoneStat label="Services planifiés" value={servicesValue} testId="wizard-done-services" accent="#10B981" />
          <DoneStat label="Auto-affectées" value={autoValue} testId="wizard-done-auto" accent="#0EA5E9" />
          <DoneStat label="Missions créées" value={missionsValue} testId="wizard-done-missions" accent="#FFC60A" />
        </div>
        {rep.quality_score != null && (
          <div className={`max-w-2xl mx-auto mb-4 border px-4 py-3 ${rep.quality_score >= 95 ? "bg-emerald-50 border-emerald-300" : rep.quality_score >= 80 ? "bg-amber-50 border-amber-300" : "bg-red-50 border-red-300"}`} data-testid="wizard-done-quality">
            <div className="text-sm font-black">
              Qualité du fichier : {rep.quality_score} %
              {rep.quality_anomaly_rows > 0 && (
                <span className="ml-2 font-normal text-xs text-zinc-600">
                  ({rep.quality_anomaly_rows} ligne{rep.quality_anomaly_rows > 1 ? "s" : ""} avec anomalie sur {rep.rows_read} lues)
                </span>
              )}
            </div>
            <div className="mt-1 text-[11px] text-zinc-700 font-mono">
              ✓ {rep.sheets_imported || 0} onglets analysés · ✓ {rep.planned_services_created || 0} services générés · ✓ {rep.missions_created || 0} missions créées
            </div>
          </div>
        )}
        {rep.duration_seconds != null && (
          <div className="max-w-2xl mx-auto text-center text-[11px] font-mono text-zinc-500 mb-4" data-testid="wizard-done-duration">
            Import réalisé en {rep.duration_seconds}s
            {rep.rows_read != null && <> · {rep.rows_read} lignes lues</>}
            {rep.rows_ignored > 0 && <> · {rep.rows_ignored} ignorée{rep.rows_ignored > 1 ? "s" : ""}</>}
          </div>
        )}
        {(rep.sheets_detail || []).length > 0 && (
          <div className="max-w-2xl mx-auto mb-5" data-testid="wizard-done-sheets-detail">
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">Détail par onglet</div>
            <table className="w-full text-[11px] border border-zinc-200 bg-white">
              <thead>
                <tr className="bg-zinc-100 text-left text-[10px] font-mono uppercase tracking-wider text-zinc-600">
                  <th className="px-2 py-1">Onglet</th>
                  <th className="px-2 py-1 text-right">Lignes lues</th>
                  <th className="px-2 py-1 text-right">Ignorées</th>
                  <th className="px-2 py-1 text-right">Variantes</th>
                  <th className="px-2 py-1 text-right">Services</th>
                  <th className="px-2 py-1 text-right">Erreurs</th>
                </tr>
              </thead>
              <tbody>
                {rep.sheets_detail.map((s) => (
                  <tr key={s.sheet} className="border-t border-zinc-100" data-testid={`wizard-done-sheet-${s.sheet}`}>
                    <td className="px-2 py-1 font-bold">{s.sheet}</td>
                    <td className="px-2 py-1 text-right font-mono">{s.rows_read}</td>
                    <td className="px-2 py-1 text-right font-mono">{s.rows_ignored || 0}</td>
                    <td className="px-2 py-1 text-right font-mono">{s.variants}</td>
                    <td className="px-2 py-1 text-right font-mono">{s.services_created}</td>
                    <td className={`px-2 py-1 text-right font-mono ${s.errors ? "text-red-600 font-bold" : "text-zinc-400"}`}>{s.errors || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {(rep.errors_detailed || []).length > 0 && (
          <div className="max-w-2xl mx-auto mb-5 border border-red-300 bg-red-50 p-3" data-testid="wizard-done-errors">
            <div className="text-[10px] font-mono uppercase tracking-widest text-red-700 mb-1.5">
              {rep.errors_detailed.length} erreur{rep.errors_detailed.length > 1 ? "s" : ""} de données détectée{rep.errors_detailed.length > 1 ? "s" : ""}
            </div>
            <div className="max-h-44 overflow-y-auto">
              <table className="w-full text-[10px]">
                <thead>
                  <tr className="text-left text-red-800 font-mono uppercase tracking-wider">
                    <th className="pr-2 pb-1">Onglet</th>
                    <th className="pr-2 pb-1">Ligne</th>
                    <th className="pr-2 pb-1">Colonne</th>
                    <th className="pr-2 pb-1">Valeur reçue</th>
                    <th className="pr-2 pb-1">Attendu</th>
                    <th className="pb-1">Correction</th>
                  </tr>
                </thead>
                <tbody>
                  {rep.errors_detailed.map((e, i) => (
                    <tr key={i} className="border-t border-red-100 align-top">
                      <td className="pr-2 py-0.5 font-bold">{e.sheet}</td>
                      <td className="pr-2 py-0.5 font-mono">L{e.row}</td>
                      <td className="pr-2 py-0.5">{e.column}</td>
                      <td className="pr-2 py-0.5 font-mono">{String(e.value)}</td>
                      <td className="pr-2 py-0.5">{e.expected}</td>
                      <td className="py-0.5 text-red-700">{e.fix}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        {(rep.pending_assignment_tours || []).length > 0 && (
          <div className="max-w-2xl mx-auto mb-5 border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 inline-flex items-start gap-2"
                data-testid="wizard-done-pending-assignment">
            <AlertTriangle size={14} className="mt-0.5" />
            <div>
              <strong>{rep.pending_assignment_tours.length} tournée{rep.pending_assignment_tours.length > 1 ? "s" : ""} sans bus principal :</strong>{" "}
              {rep.pending_assignment_tours.join(", ")}
              <div className="text-[11px] mt-0.5">Affectez-les en une fois dans <a href="/admin/fleet-assignments" className="underline font-bold">Affectations flotte → Affectation groupée</a>.</div>
            </div>
          </div>
        )}
        {g.pending > 0 && (
          <div className="max-w-2xl mx-auto mb-5 border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 inline-flex items-center gap-2"
                data-testid="wizard-done-pending">
            <AlertTriangle size={14} />
            {g.pending} mission{g.pending > 1 ? "s" : ""} en attente d&apos;affectation — visibles dans le filtre <strong>« À affecter »</strong> du Mission Control Center.
          </div>
        )}
        <div className="max-w-2xl mx-auto flex flex-col sm:flex-row gap-2 justify-center" data-testid="wizard-done-cta">
          <a href="/admin/missions" data-testid="wizard-done-cta-missions"
              className="px-4 py-2 bg-zinc-900 text-white border border-zinc-900 hover:bg-[#FFC60A] hover:text-zinc-900 text-[11px] font-black uppercase tracking-widest inline-flex items-center justify-center gap-2">
            Voir les missions <ArrowRight size={12} />
          </a>
          <a href="/admin/fleet-assignments" data-testid="wizard-done-cta-fleet"
              className="px-4 py-2 border border-zinc-900 text-zinc-900 hover:bg-zinc-100 text-[11px] font-black uppercase tracking-widest inline-flex items-center justify-center gap-2">
            Voir les affectations <LinkIcon size={12} />
          </a>
        </div>
      </div>
      <footer className="px-4 py-3 border-t border-zinc-200 bg-zinc-50 flex items-center gap-2 flex-shrink-0">
        <div className="flex-1" />
        <button onClick={onClose} data-testid="wizard-done-close"
          className="px-4 py-1.5 bg-zinc-900 text-white border border-zinc-900 hover:bg-[#FFC60A] hover:text-zinc-900 text-[11px] font-black uppercase tracking-widest">
          Fermer
        </button>
      </footer>
    </>
  );
}

function DoneStat({ label, value, accent, testId }) {
  return (
    <div className="border border-zinc-200 bg-white p-3 text-center" data-testid={testId}>
      <div className="text-[9px] font-mono uppercase tracking-[0.25em] text-zinc-500">{label}</div>
      <div className="font-display font-black text-3xl tracking-tighter leading-none mt-1"
            style={accent ? { color: accent } : undefined}>{value}</div>
    </div>
  );
}

/* ── Vehicle auto-detection summary table ────────────────────────────── */
function VehicleMatchPanel({ blocks, summary }) {
  const counts = summary || { exact: 0, multiple: 0, none: 0 };
  const total = blocks.length;
  return (
    <div className="px-4 py-3 border-b border-zinc-200 bg-sky-50/40" data-testid="wizard-vehicle-match-panel">
      <div className="flex items-center gap-3 mb-2 flex-wrap">
        <div className="font-bold text-xs inline-flex items-center gap-1.5 text-sky-900">
          <Bus size={13} /> Détection automatique du véhicule
        </div>
        <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest">
          <span className="px-1.5 py-0.5 bg-emerald-100 text-emerald-800 border border-emerald-300 inline-flex items-center gap-1" data-testid="vm-count-exact">
            <CheckCircle2 size={10} /> {counts.exact}/{total} Auto
          </span>
          {counts.multiple > 0 && (
            <span className="px-1.5 py-0.5 bg-amber-100 text-amber-900 border border-amber-300 inline-flex items-center gap-1" data-testid="vm-count-multi">
              <AlertTriangle size={10} /> {counts.multiple} À confirmer
            </span>
          )}
          {counts.none > 0 && (
            <span className="px-1.5 py-0.5 bg-red-100 text-red-800 border border-red-300 inline-flex items-center gap-1" data-testid="vm-count-none">
              <AlertCircle size={10} /> {counts.none} Manuel
            </span>
          )}
        </div>
      </div>
      <div className="border border-zinc-200 bg-white">
        <table className="w-full text-xs" data-testid="vm-table">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
              <th className="text-left px-2 py-1.5">Tournée</th>
              <th className="text-left px-2 py-1.5">Véhicule détecté</th>
              <th className="text-left px-2 py-1.5">Statut</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {blocks.map((b, i) => {
              const vm = b.vehicle_match || { status: "none", matches: [] };
              const code = `${b.service_code || "?"}${b.service_letter || ""}`;
              const day = b.day_name || DAY_NAMES[b.day_index] || "?";
              const query = vm.query || b.client_name_hint || b.client_name || "—";
              return (
                <tr key={i} data-testid={`vm-row-${i}`}>
                  <td className="px-2 py-1.5">
                    <span className="font-mono font-bold">{code}</span>
                    <span className="text-zinc-500 ml-1">{day}</span>
                  </td>
                  <td className="px-2 py-1.5">
                    {vm.status === "exact" ? (
                      <span className="inline-flex items-center gap-1 font-medium">
                        <LinkIcon size={10} className="text-emerald-600" />
                        {vm.matches[0]?.label || vm.matches[0]?.model || query}
                      </span>
                    ) : vm.status === "multiple" ? (
                      <span className="text-amber-800">
                        {vm.matches.map((m) => m.label || m.model).join(", ") || query}
                        <span className="text-[10px] text-zinc-500 ml-1">({vm.matches.length} candidats)</span>
                      </span>
                    ) : (
                      <span className="text-zinc-500 italic">Aucun ({query})</span>
                    )}
                  </td>
                  <td className="px-2 py-1.5">
                    {vm.status === "exact" ? (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-emerald-100 text-emerald-800 border border-emerald-300 text-[10px] font-mono font-bold uppercase tracking-widest">
                        <CheckCircle2 size={10} /> Affectation automatique
                      </span>
                    ) : vm.status === "multiple" ? (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-amber-100 text-amber-900 border border-amber-300 text-[10px] font-mono font-bold uppercase tracking-widest">
                        <AlertTriangle size={10} /> Sélection manuelle
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-red-100 text-red-800 border border-red-300 text-[10px] font-mono font-bold uppercase tracking-widest">
                        <AlertCircle size={10} /> Affectation manuelle
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="text-[10px] text-zinc-500 mt-1.5">
        Les tournées reconnues seront automatiquement affectées au véhicule correspondant lors de la confirmation. Les autres devront être affectées manuellement dans <strong>Affectations flotte</strong>.
      </div>
    </div>
  );
}


function KpiTile({ label, value, tone, testId }) {
  return (
    <div data-testid={testId}>
      <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={`font-display font-black text-xl leading-none tracking-tighter mt-0.5 ${tone === "warn" ? "text-amber-700" : "text-zinc-900"}`}>{value}</div>
    </div>
  );
}

function Mini({ label, value, tone }) {
  return (
    <div>
      <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={`text-sm font-bold ${tone === "warn" ? "text-amber-700" : "text-zinc-900"}`}>{value}</div>
    </div>
  );
}

function BlockPreview({ block, index, expanded, onToggle }) {
  // Normalise both legacy (block.entries) and smart (block.stops + block.events) shapes.
  let entries = block.entries;
  if (!entries) {
    const all = [...(block.stops || []), ...(block.events || [])];
    entries = all.sort((a, b) => (a.sequence || 0) - (b.sequence || 0));
  }
  const stops = entries.filter((e) => e.kind === "stop");
  const events = entries.filter((e) => e.kind === "event");
  const Chevron = expanded ? ChevronDown : ChevronRight;
  const code = `${block.service_code || "?"}${block.service_letter || ""}`;
  const dayName = block.day_name || (DAY_NAMES[block.day_index] || "?");
  const direction = block.direction_hint || block.direction;
  const blockWarnings = block.warnings || [];
  return (
    <div data-testid={`wizard-block-${index}`}>
      <button onClick={onToggle}
        className="w-full px-4 py-2 flex items-center gap-3 hover:bg-zinc-50 text-left">
        <Chevron size={14} className="text-zinc-500 flex-shrink-0" />
        <Calendar size={14} className="text-zinc-500" />
        <div className="font-bold text-sm">{dayName}</div>
        <div className="px-2 py-0.5 bg-zinc-900 text-[#FFC60A] font-mono text-xs font-bold">{code}</div>
        {direction && (
          <span className="px-1.5 py-0.5 text-[9px] font-mono font-bold uppercase tracking-widest border border-sky-300 bg-sky-50 text-sky-700">
            {direction === "aller" ? "Aller" : direction === "retour" ? "Retour" : direction}
          </span>
        )}
        {blockWarnings.length > 0 && (
          <span className="px-1.5 py-0.5 text-[9px] font-mono font-bold uppercase tracking-widest border border-amber-300 bg-amber-50 text-amber-700 inline-flex items-center gap-1">
            <AlertTriangle size={9} /> {blockWarnings.length}
          </span>
        )}
        <div className="flex-1" />
        <span className="text-[10px] font-mono text-zinc-500">{block.vehicle_type || ""} {block.vehicle_capacity || ""}</span>
        <span className="text-[10px] font-mono text-zinc-500">Amp {block.amplitude || "—"} · Temps {block.work_time || "—"}</span>
        <span className="text-[10px] font-mono text-zinc-500">{stops.length} arrêts · {events.length} évts</span>
      </button>
      {expanded && (
        <div className="px-4 pb-3 bg-zinc-50">
          <table className="w-full text-xs">
            <thead className="bg-white border-b border-zinc-200">
              <tr className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                <th className="text-left px-2 py-1.5 w-10">#</th>
                <th className="text-left px-2 py-1.5">Type</th>
                <th className="text-left px-2 py-1.5">Arrêt / Événement</th>
                <th className="text-left px-2 py-1.5 w-16">Arr.</th>
                <th className="text-left px-2 py-1.5 w-16">Dép.</th>
                <th className="text-right px-2 py-1.5 w-12">+</th>
                <th className="text-right px-2 py-1.5 w-12">−</th>
                <th className="text-left px-2 py-1.5">Remarque / Direction</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {entries.map((e, i) => <EntryRow key={i} e={e} />)}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function EntryRow({ e }) {
  if (e.kind === "event") {
    const meta = EVENT_META[e.name] || { icon: AlertCircle, color: "#71717A", label: e.name };
    const Icon = meta.icon;
    return (
      <tr className="bg-zinc-100/60" data-testid={`entry-event-${e.sequence}`}>
        <td className="px-2 py-1.5 font-mono text-zinc-500">{e.sequence}</td>
        <td className="px-2 py-1.5 font-mono text-[10px] uppercase tracking-widest text-zinc-500 inline-flex items-center gap-1"><Icon size={11} style={{ color: meta.color }} /> Événement</td>
        <td className="px-2 py-1.5 font-bold" style={{ color: meta.color }}>{meta.label}</td>
        <td className="px-2 py-1.5 font-mono">{e.start || "—"}</td>
        <td className="px-2 py-1.5 font-mono">{e.end || "—"}</td>
        <td className="px-2 py-1.5"></td>
        <td className="px-2 py-1.5"></td>
        <td className="px-2 py-1.5 text-zinc-500">{e.remark || ""}</td>
      </tr>
    );
  }
  const inline = e.service_event_inline;
  const inlineColor = inline === "Prise de service" ? "#10B981" : inline === "Fin de service" ? "#DC2626" : inline === "HLP" ? "#A855F7" : null;
  return (
    <tr data-testid={`entry-stop-${e.sequence}`}>
      <td className="px-2 py-1.5 font-mono text-zinc-500">{e.sequence}</td>
      <td className="px-2 py-1.5 font-mono text-[10px] uppercase tracking-widest text-zinc-500 inline-flex items-center gap-1"><MapPin size={11} className="text-zinc-400" /> Arrêt</td>
      <td className="px-2 py-1.5">
        <span className="font-medium">{e.name}</span>
        {inline && <span className="ml-2 px-1.5 py-0.5 text-[9px] font-mono font-bold uppercase tracking-widest border" style={{ color: inlineColor, borderColor: inlineColor }}>{inline}</span>}
      </td>
      <td className="px-2 py-1.5 font-mono">{e.arrival || "—"}</td>
      <td className="px-2 py-1.5 font-mono">{e.departure || "—"}</td>
      <td className="px-2 py-1.5 font-mono text-right text-emerald-700 font-bold">{e.boarding ?? (e.boarding_unknown ? "?" : "—")}</td>
      <td className="px-2 py-1.5 font-mono text-right text-amber-700 font-bold">{e.alighting ?? (e.alighting_unknown ? "?" : "—")}</td>
      <td className="px-2 py-1.5 text-zinc-600 truncate max-w-[260px]">
        {e.remark}
        {e.direction && <span className="ml-1 text-zinc-400">→ {e.direction}</span>}
      </td>
    </tr>
  );
}

/* ── Program detail drawer ─────────────────────────────────────────────── */
function ProgramDetail({ templateId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(0);
  const [genOpen, setGenOpen] = useState(false);
  useEffect(() => {
    setLoading(true);
    api.get(`/programs/${templateId}`)
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message))
      .finally(() => setLoading(false));
  }, [templateId]);
  const blocks = useMemo(() => (data?.days || []).map((d) => ({
    ...d,
    entries: [
      ...(d.stops || []).map((s) => ({ ...s, kind: "stop" })),
      ...(d.events || []).map((ev) => ({ ...ev, kind: "event" })),
    ].sort((a, b) => (a.sequence || 0) - (b.sequence || 0)),
  })), [data]);
  const tpl = data?.template;
  return (
    <div className="fixed inset-0 z-[1000] bg-black/60 flex items-center justify-center p-4" data-testid="program-detail">
      <div className="bg-white border-2 border-zinc-900 shadow-[6px_6px_0_0_#FFC60A] w-full max-w-6xl max-h-[92vh] flex flex-col">
        <header className="px-4 py-3 bg-zinc-900 text-white flex items-center gap-3 flex-shrink-0">
          <Eye size={16} className="text-[#FFC60A]" />
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#FFC60A]">Programme</div>
            <div className="font-display font-black text-lg tracking-tighter truncate">{tpl?.program_name || "Chargement…"}</div>
          </div>
          {tpl && (
            <button onClick={() => setGenOpen(true)} data-testid="program-generate-open"
              className="px-3 py-1.5 bg-[#FFC60A] text-zinc-900 border border-[#FFC60A] hover:bg-yellow-300 text-[11px] font-black uppercase tracking-widest inline-flex items-center gap-1.5">
              <Zap size={12} /> Générer l&apos;année
            </button>
          )}
          <button onClick={onClose} data-testid="program-detail-close" className="text-zinc-400 hover:text-white"><X size={18} /></button>
        </header>
        {loading ? (
          <div className="p-12 text-center"><Loader2 size={20} className="inline animate-spin mr-2" /> Chargement…</div>
        ) : (
          <>
            <div className="px-4 py-3 border-b border-zinc-200 bg-zinc-50 flex items-center gap-4 flex-wrap">
              <Mini label="Client" value={tpl?.client_name} />
              <Mini label="Version" value={tpl?.version || "—"} />
              <Mini label="Jours" value={blocks.length} />
              <Mini label="Arrêts uniques" value={tpl?.stops_count} />
              <Mini label="Importé le" value={(tpl?.created_at || "").slice(0, 10)} />
            </div>
            <div className="flex-1 overflow-y-auto divide-y divide-zinc-200">
              {blocks.map((b, i) => (
                <BlockPreview key={b.id || i} block={b} index={i} expanded={expanded === i} onToggle={() => setExpanded(expanded === i ? -1 : i)} />
              ))}
            </div>
          </>
        )}
      </div>
      {genOpen && tpl && (
        <GenerateDialog templateId={templateId} program={tpl}
          onClose={() => setGenOpen(false)}
          onDone={() => { setGenOpen(false); toast.success("Services générés"); }} />
      )}
    </div>
  );
}

/* ── Generation dialog: pick year + range + dry_run preview + confirm ──── */
function GenerateDialog({ templateId, program, onClose, onDone }) {
  const [years, setYears] = useState([]);
  const [year, setYear] = useState("");
  const [fromIso, setFromIso] = useState("");
  const [toIso, setToIso] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    api.get("/calendar/school-years")
      .then(({ data }) => {
        const list = data.years || [];
        setYears(list);
        if (list.length) {
          setYear(list[0].year);
          setFromIso(list[0].first_day);
          setToIso(list[0].last_day);
        }
      })
      .catch((e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message));
  }, []);
  // Re-align bounds when year changes
  useEffect(() => {
    const y = years.find((y) => y.year === year);
    if (y) { setFromIso(y.first_day); setToIso(y.last_day); }
  }, [year, years]);

  const runPreview = async () => {
    if (!year) return;
    setBusy(true); setPreview(null);
    try {
      const { data } = await api.post(`/programs/${templateId}/generate`, {
        year, from_iso: fromIso, to_iso: toIso, dry_run: true,
      });
      setPreview(data);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  const runGenerate = async () => {
    if (!preview) return;
    if (!window.confirm(`Confirmer la génération de ${preview.created_count} services planifiés ?`)) return;
    setGenerating(true);
    try {
      const { data } = await api.post(`/programs/${templateId}/generate`, {
        year, from_iso: fromIso, to_iso: toIso, dry_run: false,
      });
      toast.success(`${data.persisted_count || data.created_count} services planifiés persistés`);
      onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message); }
    finally { setGenerating(false); }
  };
  return (
    <div className="fixed inset-0 z-[1100] bg-black/70 flex items-center justify-center p-4" data-testid="generate-dialog">
      <div className="bg-white border-2 border-zinc-900 shadow-[6px_6px_0_0_#FFC60A] w-full max-w-3xl max-h-[92vh] flex flex-col">
        <header className="px-4 py-3 bg-zinc-900 text-white flex items-center gap-3 flex-shrink-0">
          <Zap size={16} className="text-[#FFC60A]" />
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#FFC60A]">Génération</div>
            <div className="font-display font-black text-lg tracking-tighter truncate">{program.program_name}</div>
          </div>
          <button onClick={onClose} data-testid="generate-close" className="text-zinc-400 hover:text-white"><X size={18} /></button>
        </header>
        <div className="px-4 py-3 border-b border-zinc-200 bg-zinc-50">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Field label="Année scolaire">
              <select value={year} onChange={(e) => setYear(e.target.value)} data-testid="generate-year"
                className="w-full border border-zinc-300 px-2 py-1.5 text-sm bg-white">
                {years.map((y) => <option key={y.year} value={y.year}>{y.year}</option>)}
              </select>
            </Field>
            <Field label="Du">
              <input type="date" value={fromIso} onChange={(e) => setFromIso(e.target.value)} data-testid="generate-from"
                className="w-full border border-zinc-300 px-2 py-1.5 text-sm" />
            </Field>
            <Field label="Au">
              <input type="date" value={toIso} onChange={(e) => setToIso(e.target.value)} data-testid="generate-to"
                className="w-full border border-zinc-300 px-2 py-1.5 text-sm" />
            </Field>
          </div>
          <div className="mt-3 flex items-center gap-2">
            <button onClick={runPreview} disabled={busy || !year} data-testid="generate-preview"
              className="px-3 py-1.5 border border-zinc-900 hover:bg-zinc-100 text-[11px] font-bold uppercase tracking-widest inline-flex items-center gap-1.5 disabled:opacity-50">
              {busy ? <Loader2 size={11} className="animate-spin" /> : <Eye size={11} />} Simuler
            </button>
            <span className="text-[10px] font-mono text-zinc-500">
              La simulation ne crée rien. Confirmez après vérification.
            </span>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-4" data-testid="generate-preview-body">
          {!preview ? (
            <div className="text-center text-sm text-zinc-400 py-10">
              <Calendar size={28} className="mx-auto mb-2 text-zinc-300" />
              Choisissez une année scolaire puis cliquez sur <strong>Simuler</strong>.
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
                <Big label="Services planifiés" value={preview.created_count} accent="#10B981" testId="gen-kpi-count" />
                <Big label="Jours travaillés" value={preview.calendar?.working_days_count} testId="gen-kpi-workdays" />
                <Big label="Jours exclus" value={preview.calendar?.excluded_days_count} accent="#F59E0B" testId="gen-kpi-excluded" />
                <Big label="GTFS liés" value={`${preview.gtfs_links?.matched || 0}/${preview.created_count || 0}`} accent={(preview.gtfs_links?.matched || 0) > 0 ? "#3B82F6" : "#71717A"} testId="gen-kpi-gtfs" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <BreakdownCard title="Par jour de semaine" data={preview.by_day_index} testId="gen-by-day" mapper={(k) => ["Lundi","Mardi","Mercredi","Jeudi","Vendredi","Samedi","Dimanche"][parseInt(k, 10)] || k} />
                <BreakdownCard title="Par service" data={preview.by_service_code} testId="gen-by-code" mapper={(k) => `Service ${k}`} />
              </div>
              {preview.alerts && (
                <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-2" data-testid="gen-alerts">
                  <AlertChip label="Non assignés" value={preview.alerts.unassigned} icon={AlertCircle} tone="warn" />
                  <AlertChip label="Sans lien GTFS" value={preview.alerts.no_gtfs_link} icon={MapPinned} tone="warn" />
                  <AlertChip label="Arrêts inconnus" value={preview.alerts.unknown_stops} icon={MapPin} tone={preview.alerts.unknown_stops ? "warn" : "ok"} />
                </div>
              )}
              {preview.gtfs_links?.unknown_stops?.length > 0 && (
                <div className="mt-4 p-3 bg-amber-50 border border-amber-200" data-testid="gen-unknown-stops">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-amber-800 font-bold mb-1">
                    Arrêts inconnus du référentiel ({preview.gtfs_links.unknown_stops.length})
                  </div>
                  <ul className="text-xs grid grid-cols-2 gap-x-3 gap-y-0.5">
                    {preview.gtfs_links.unknown_stops.map((s) => (
                      <li key={s} className="text-amber-900"><MapPin size={9} className="inline" /> {s}</li>
                    ))}
                  </ul>
                  <p className="text-[10px] text-amber-700 mt-1.5">
                    Ces arrêts seront stockés avec un nom mais ne pourront pas être positionnés sur la carte tant qu&apos;ils ne sont pas ajoutés dans Arrêts.
                  </p>
                </div>
              )}
            </>
          )}
        </div>
        <div className="px-4 py-3 border-t border-zinc-200 bg-zinc-50 flex items-center gap-2 flex-shrink-0">
          <button onClick={onClose} disabled={generating} data-testid="generate-cancel"
            className="px-3 py-1.5 border border-zinc-300 hover:bg-white text-[11px] font-bold uppercase tracking-widest">
            Annuler
          </button>
          <div className="flex-1" />
          <button onClick={runGenerate} disabled={!preview || generating}
            data-testid="generate-confirm"
            className="px-4 py-1.5 bg-zinc-900 text-white border border-zinc-900 hover:bg-[#FFC60A] hover:text-zinc-900 text-[11px] font-black uppercase tracking-widest inline-flex items-center gap-1.5 disabled:opacity-50">
            {generating ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />} Générer {preview ? `(${preview.created_count})` : ""}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">{label}</div>
      {children}
    </div>
  );
}

function Big({ label, value, accent, testId }) {
  return (
    <div className="border border-zinc-200 bg-white p-2" data-testid={testId}>
      <div className="text-[9px] font-mono uppercase tracking-[0.25em] text-zinc-500">{label}</div>
      <div className="font-display font-black text-2xl tracking-tighter leading-none mt-0.5" style={accent ? { color: accent } : undefined}>{value ?? "—"}</div>
    </div>
  );
}

function BreakdownCard({ title, data, mapper, testId }) {
  const entries = Object.entries(data || {});
  const max = Math.max(1, ...entries.map(([, v]) => v));
  return (
    <div className="border border-zinc-200 bg-white" data-testid={testId}>
      <div className="px-2 py-1.5 bg-zinc-50 border-b border-zinc-200 text-[10px] font-mono uppercase tracking-widest font-bold">{title}</div>
      <div className="p-2 space-y-1">
        {entries.length === 0 ? (
          <div className="text-xs text-zinc-400 text-center py-2">—</div>
        ) : entries.map(([k, v]) => (
          <div key={k} className="flex items-center gap-2 text-xs">
            <span className="w-20 truncate text-zinc-600">{mapper(k)}</span>
            <div className="flex-1 h-3 bg-zinc-100 relative">
              <div className="absolute inset-y-0 left-0 bg-[#FFC60A]" style={{ width: `${(v / max) * 100}%` }} />
            </div>
            <span className="font-mono font-bold w-10 text-right">{v}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function AlertChip({ label, value, icon: Icon, tone }) {
  const colors = tone === "warn" && value > 0 ? "border-amber-300 bg-amber-50 text-amber-900"
    : tone === "ok" || !value ? "border-emerald-300 bg-emerald-50 text-emerald-900"
    : "border-zinc-200 bg-white text-zinc-700";
  return (
    <div className={`border ${colors} px-3 py-2 flex items-center gap-2`}>
      <Icon size={14} />
      <div className="flex-1 min-w-0">
        <div className="text-[10px] font-mono uppercase tracking-widest opacity-70">{label}</div>
        <div className="font-bold text-sm">{value}</div>
      </div>
    </div>
  );
}
